import { useEffect } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import history from "utils/history";
import viewpaths from "global/constants/ViewPathConstants";
import { handleErrorMessage, openSuccessNotification } from "helpers/methods";
import { exchangeAccessToken } from "./CloudStorageService";
import { getProviderByKey } from "./providers";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";

export const AUTH_CODE_PROVIDER_KEY = "cloudStorageProvider";
export const AUTO_OPEN_PROVIDER_KEY = "cloudStorageOpenPicker";

// Module-scoped so it survives React StrictMode double-invoke and any route
// remounts. Each OAuth code is single-use on the backend — firing it twice
// makes the second call fail with "OAuth state expired".
const processedCodes = new Set<string>();

const CloudStorageOAuthCallback = () => {
  useTitle(title.CLOUD_STORAGE_CONNECT);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state") ?? params.get("session_state") ?? "";
    const providerKey = sessionStorage.getItem(AUTH_CODE_PROVIDER_KEY);

    const goToCreate = () =>
      history.replace(viewpaths.createSignRequestsViewPath);

    if (!code || !providerKey || code === "null") {
      goToCreate();
      return;
    }

    if (processedCodes.has(code)) {
      // Already exchanged (or in-flight) for this code — skip.
      return;
    }
    processedCodes.add(code);

    const provider = getProviderByKey(providerKey);
    if (!provider) {
      sessionStorage.removeItem(AUTH_CODE_PROVIDER_KEY);
      goToCreate();
      return;
    }

    (async () => {
      try {
        await exchangeAccessToken(providerKey, code, state);
        openSuccessNotification(`${provider.displayName} connected!`);
        sessionStorage.setItem(AUTO_OPEN_PROVIDER_KEY, providerKey);
      } catch (error: any) {
        handleErrorMessage(error);
      } finally {
        sessionStorage.removeItem(AUTH_CODE_PROVIDER_KEY);
        goToCreate();
      }
    })();
  }, []);

  return (
    <Box
      role="status"
      aria-live="polite"
      sx={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
      }}
    >
      <CircularProgress aria-hidden="true" />
      <Typography variant="body1" color="text.secondary">
        Connecting your cloud storage…
      </Typography>
    </Box>
  );
};

export default CloudStorageOAuthCallback;
